import { RequirementActivity, RequirementActivityType } from '../../../models/requirement-activity.model';

/** Ein Ereignis der Anforderungs-Historie, aufbereitet für die Anzeige (Historie-Seite und Reiter „Verlauf“). */
export interface RequirementActivityItem {
  type: RequirementActivityType;
  user: string | null;
  createdAt: string;
  field: string | null;
  from: string | null;
  to: string | null;
  fieldKey: string | null;
  fieldParams: Record<string, number> | null;
  fromKey: string | null;
  toKey: string | null;
  message: string | null;
}

export type Uebersetzer = (key: string, params?: Record<string, number>) => string;

const FIELD_KEYS = new Map<string, string>(
  [
    'title',
    'description',
    'type',
    'priority',
    'status',
    'owner',
    'version',
    'notes',
    'tags',
    'automationStatus',
    'testFramework',
    'providerLink',
    'criteria',
    'testCase',
  ].map((name) => [name, `requirements.history.fields.${name}`]),
);

const VALUE_KEYS = new Map<string, Map<string, string>>([
  [
    'priority',
    new Map([
      ['LOW', 'requirements.priority.LOW'],
      ['MEDIUM', 'requirements.priority.MEDIUM'],
      ['HIGH', 'requirements.priority.HIGH'],
      ['CRITICAL', 'requirements.priority.CRITICAL'],
    ]),
  ],
  [
    'automationStatus',
    new Map([
      ['MANUAL', 'requirements.form.automation.status.manual'],
      ['AUTOMATABLE', 'requirements.form.automation.status.automatable'],
      ['AUTOMATED', 'requirements.form.automation.status.automated'],
    ]),
  ],
  [
    'criteriaChecked',
    new Map([
      ['false', 'requirements.history.values.criteriaChecked.false'],
      ['true', 'requirements.history.values.criteriaChecked.true'],
    ]),
  ],
]);

const CRITERIA_FIELDS = ['criteria', 'criteriaChecked'];

const BOTH_SIDES_TYPES: RequirementActivityType[] = ['EDITED', 'STATUS_CHANGED'];
const EMPTY_VALUE = '—';

export function toRequirementActivityItem(activity: RequirementActivity): RequirementActivityItem {
  const payload = activity.payload ?? {};
  if (activity.type === 'HANDED_OVER') return handoverItem(activity, payload);
  const field = asText(payload['field']);
  const valueKeys = VALUE_KEYS.get(field ?? '');
  const placeholder = field && BOTH_SIDES_TYPES.includes(activity.type) ? EMPTY_VALUE : null;
  const from = side(field, payload['from'], payload['fromName'], valueKeys, placeholder);
  const to = side(field, payload['to'], payload['toName'], valueKeys, placeholder);
  const criterionPosition = position(field, payload['position']);
  return {
    type: activity.type,
    user: activity.createdByName ?? null,
    createdAt: activity.createdAt,
    field,
    from: from.text,
    to: to.text,
    fieldKey: criterionPosition ? 'requirements.history.fields.criteriaAt' : fieldKey(field),
    fieldParams: criterionPosition ? { position: criterionPosition } : null,
    fromKey: from.key,
    toKey: to.key,
    message: null,
  };
}

/** „{vorher} → {an}“ aus den beim Lesen ergänzten Namen; ohne Vorstufe mit Person nur die neue Person. */
function handoverItem(activity: RequirementActivity, payload: Record<string, unknown>): RequirementActivityItem {
  const from = person(payload['fromUserId'], payload['fromName']);
  const to = person(payload['toUserId'], payload['toName']);
  return {
    type: activity.type,
    user: activity.createdByName ?? null,
    createdAt: activity.createdAt,
    field: null,
    from: from.text,
    to: to.text,
    fieldKey: null,
    fieldParams: null,
    fromKey: from.key,
    toKey: to.key,
    message: asText(payload['message']),
  };
}

function person(id: unknown, name: unknown): { text: string | null; key: string | null } {
  if (!asText(id)) return { text: null, key: null };
  const text = asText(name);
  return text ? { text, key: null } : { text: null, key: 'common.unknownUser' };
}

/** Feld und Werte in einer Zeile, etwa „Priorität: Niedrig → Hoch“; ohne Feld und Werte `null`. */
export function activityDetailText(item: RequirementActivityItem, uebersetze: Uebersetzer): string | null {
  const feld = item.field
    ? item.fieldKey
      ? uebersetze(item.fieldKey, item.fieldParams ?? undefined)
      : item.field
    : null;
  const vorher = item.fromKey ? uebersetze(item.fromKey) : item.from;
  const nachher = item.toKey ? uebersetze(item.toKey) : item.to;
  const werte = [vorher, nachher].filter((wert): wert is string => !!wert).join(' → ');
  if (feld && werte) return `${feld}: ${werte}`;
  return feld || werte || null;
}

function side(
  field: string | null,
  value: unknown,
  name: unknown,
  valueKeys: Map<string, string> | undefined,
  placeholder: string | null,
): { text: string | null; key: string | null } {
  const text = asText(value);
  if (!text) {
    return { text: placeholder, key: null };
  }
  if (field === 'owner') {
    return person(value, name);
  }
  return { text, key: valueKeys?.get(text) ?? null };
}

function position(field: string | null, value: unknown): number | null {
  return CRITERIA_FIELDS.includes(field ?? '') && Number.isInteger(value) && (value as number) >= 1
    ? (value as number)
    : null;
}

function fieldKey(field: string | null): string | null {
  return field === 'criteriaChecked' ? 'requirements.history.fields.criteria' : (FIELD_KEYS.get(field ?? '') ?? null);
}

function asText(value: unknown): string | null {
  return value === undefined || value === null ? null : String(value);
}
